<template>
  <PageLayout
    :page-title="bannerPageTitle"
    :breadcrumb="bannerBreadcrumb"
    :breadcrumb-to="mode === 'case' ? '/case/' : '/solution/'"
    banner-key="solution"
  >
    <div class="solutionDiv case-page">
      <div class="case-page__inner wrap">
        <header class="case-page__head">
          <h1 class="case-page__title">{{ pageTitle }}</h1>
        </header>

        <CaseIndustryNav
          :mode="mode"
          :active-slug="activeSlug"
          :active-sub-slug="subSlug"
        />

        <!-- Selected case: 文字 → 图片 → 视频 -->
        <article class="case-detail" v-if="selectedCase">
          <header class="case-detail__head">
            <h1 class="case-detail__title">{{ itemTitle(selectedCase) }}</h1>
          </header>
          <div
            v-if="caseBodyHtml(selectedCase)"
            class="case-detail__content"
            v-html="caseBodyHtml(selectedCase)"
          ></div>
          <div v-if="itemImage(selectedCase)" class="case-detail__media case-detail__media--image">
            <img :src="itemImage(selectedCase)" :alt="itemTitle(selectedCase)">
          </div>
          <div v-if="selectedCaseVideo" class="case-detail__media case-detail__media--video is-video">
            <CompatibleVideo
              :src="selectedCaseVideo"
              video-class="case-detail__video"
              :controls="true"
              preload="metadata"
              @loadedmetadata="unmuteVideo"
              @play="unmuteVideo"
            />
          </div>
        </article>

        <div class="case-page__body" v-else-if="displayItems.length">
          <div class="case-tiles">
            <router-link
              v-for="item in displayItems"
              :key="item.id"
              :to="itemLink(item)"
              class="case-tile"
            >
              <span v-if="itemImage(item)" class="case-tile__media">
                <img :src="itemImage(item)" :alt="itemTitle(item)">
              </span>
              <span class="case-tile__name">{{ itemTitle(item) }}</span>
            </router-link>
          </div>
          <PagePagination
            :page="pageNum"
            :total="listTotal"
            :page-size="pageSize"
            @change="onPageChange"
          />
        </div>

        <div class="case-page__intro" v-else-if="categoryIntro">
          <p class="case-page__intro-text">{{ categoryIntro.desc }}</p>
        </div>

        <p v-else class="case-page__empty">{{ emptyMessage }}</p>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import PageLayout from '@/components/hirays/PageLayout.vue'
import CaseIndustryNav from '@/components/hirays/CaseIndustryNav.vue'
import PagePagination from '@/components/hirays/PagePagination.vue'
import CompatibleVideo from '@/components/hirays/CompatibleVideo.vue'
import { listSolutionCategory, listSolutionItem } from '@/utils/http'
import { unwrapRows, resolveImageUrl, stripHtml } from '@/utils/content'
import { resolveCmsImageUrl } from '@/utils/media'
import { slicePage, scrollToListTop } from '@/utils/pagination'

/** 4 columns × 2 rows — matches .case-tiles grid */
const CASE_PAGE_SIZE = 8
import { SOLUTION_NAV, APPLICATION_NAV } from '@/constants/hiraysHomeStatic'

const SLUG_ALIASES = {
  batteries: 'battery',
  hvac: 'hvac-refrigeration',
  medical: 'medical-devices',
  aerospace: 'defense-aerospace',
  'advanced-materials': 'new-materials',
  'wires-connectors': 'wire-connector',
  others: 'other',
  semiconductors: 'semiconductor'
}

const REVERSE_SLUG_ALIASES = Object.keys(SLUG_ALIASES).reduce((acc, key) => {
  acc[SLUG_ALIASES[key]] = key
  return acc
}, {})

const SOLUTION_NAME_SLUG = {
  Automotive: 'automotive',
  'HVAC/R': 'hvac',
  Energy: 'energy',
  Batteries: 'batteries',
  Semiconductors: 'semiconductors',
  'Consumer Electronics': 'consumer-electronics',
  'General Equipment': 'general-equipment',
  'Medical Devices': 'medical',
  'Home Appliances': 'home-appliances',
  'Aerospace & Defense': 'aerospace',
  'Advanced Materials': 'advanced-materials',
  'Wires & Connectors': 'wires-connectors',
  Others: 'others'
}

const STATIC_CASE_IMAGES = {}

export default {
  components: { PageLayout, CaseIndustryNav, PagePagination, CompatibleVideo },
  props: {
    solutionSlug: { type: String, default: '' },
    subSlug: { type: String, default: '' },
    mode: { type: String, default: 'solution' }
  },
  data() {
    return {
      categories: [],
      caseItems: [],
      subItems: [],
      pageNum: 1,
      pageSize: CASE_PAGE_SIZE
    }
  },
  computed: {
    basePath() {
      return this.mode === 'case' ? '/case/' : '/solution/'
    },
    pageTitle() {
      return this.mode === 'case' ? this.$t('nav.applications') : this.$t('nav.solutions')
    },
    bannerBreadcrumb() {
      return this.selectedCase ? this.pageTitle : ''
    },
    bannerPageTitle() {
      return this.selectedCase ? this.itemTitle(this.selectedCase) : this.pageTitle
    },
    selectedCase() {
      if (this.mode !== 'case' || !this.subSlug) return null
      const matched = this.caseItems.filter(item => this.matchesCategory(item) && this.matchesSubSlug(item))
      return matched.length === 1 ? matched[0] : null
    },
    selectedCaseVideo() {
      return this.itemVideo(this.selectedCase)
    },
    categoryType() {
      return this.mode === 'case' ? 1 : 0
    },
    navItems() {
      return this.mode === 'case' ? APPLICATION_NAV : SOLUTION_NAV
    },
    navGroup() {
      return this.mode === 'case' ? 'applicationNav' : 'solutionNav'
    },
    activeSlug() {
      if (this.solutionSlug) return this.solutionSlug
      if (this.mode === 'case') return 'all'
      return (this.navItems[0] && this.navItems[0].slug) || ''
    },
    isAllCases() {
      return this.mode === 'case' && this.activeSlug === 'all'
    },
    activeName() {
      if (this.isAllCases) {
        return this.$i18n.locale === 'zh' ? '\u5168\u90E8' : 'All'
      }
      const cat = this.findCategory(this.activeSlug)
      if (cat) return this.categoryLabel(cat)
      const item = this.navItems.find(n => n.slug === this.activeSlug)
      return item ? this.navLabel(item) : this.pageTitle
    },
    activeCategory() {
      if (this.isAllCases) return null
      return this.findCategory(this.activeSlug)
    },
    allItems() {
      let items = this.isAllCases
        ? this.caseItems.slice()
        : this.caseItems.filter(item => this.matchesCategory(item))
      if (this.subSlug) {
        items = items.filter(item => this.matchesSubSlug(item))
      }
      if (items.length) return items
      return this.staticFallbackItems
    },
    listTotal() {
      return this.allItems.length
    },
    displayItems() {
      return slicePage(this.allItems, this.pageNum, this.pageSize)
    },
    categoryIntro() {
      if (this.allItems.length) return null
      const cat = this.activeCategory
      if (cat && cat.description) {
        return {
          image: resolveImageUrl(cat.picUrl),
          desc: this.$cleanBrand(cat.description || '')
        }
      }
      if (this.mode !== 'solution') return null
      return null
    },
    staticFallbackItems() {
      return []
    },
    emptyMessage() {
      return this.mode === 'case' ? this.$t('common.noCases') : this.$t('common.noProducts')
    }
  },
  watch: {
    solutionSlug() {
      this.pageNum = 1
      this.loadItems()
    },
    subSlug() {
      this.pageNum = 1
      this.loadItems()
    },
    mode: {
      immediate: true,
      handler() {
        this.loadCategories()
      }
    }
  },
  methods: {
    unmuteVideo(e) {
      const el = e && e.target
      if (!el) return
      try {
        el.muted = false
        el.defaultMuted = false
        el.volume = 1
      } catch (err) { /* ignore */ }
    },

    onPageChange(page) {
      this.pageNum = page
      scrollToListTop(this.$el.querySelector('.case-page__body') || this.$el)
    },

    reloadCmsData() {
      this.loadCategories().then(() => this.loadItems())
    },

    normalizeSlug(slug) {
      return SLUG_ALIASES[slug] || slug
    },

    findCategory(slug) {
      const normalized = this.normalizeSlug(slug)
      return this.categories.find(c =>
        c.categorySlug === slug ||
        c.categorySlug === normalized ||
        (REVERSE_SLUG_ALIASES[c.categorySlug] || c.categorySlug) === slug
      )
    },

    navLabel(item) {
      return this.$navItemLabel(this.navGroup, item)
    },

    categoryLabel(cat) {
      return this.$navItemLabel(this.navGroup, cat)
    },

    itemTitle(item) {
      const baseName = this.$navItemLabel('solutionSubNav', item)
      const name = this.$cleanBrand(baseName)
      if (this.mode === 'case' && name && !name.includes('\u884C\u4E1A\u6848\u4F8B') && !name.includes('\u5E94\u7528\u6848\u4F8B') && !name.toLowerCase().includes('industry case') && !name.toLowerCase().includes('application case')) {
        return name + (this.$i18n.locale === 'zh' ? '-\u884C\u4E1A\u6848\u4F8B' : ' - Industry Case')
      }
      return name
    },

    itemImage(item) {
      return resolveCmsImageUrl(item.picUrl || item.image || '')
    },

    itemVideo(item) {
      const raw = (item && (item.videoUrl || item.video_url)) || ''
      return resolveImageUrl(raw) || ''
    },

    caseSummary(item) {
      const desc = stripHtml(item.description || '').trim()
      if (desc) return this.$cleanBrand(desc)
      return ''
    },

    caseBodyHtml(item) {
      let raw = item.content || item.description || ''
      raw = String(raw).replace(/<img\b[^>]*>/gi, '')
      if (!stripHtml(raw).trim()) {
        return '<p>' + this.$cleanBrand(this.itemTitle(item)) + '</p>'
      }
      return this.$cleanBrand(raw)
    },

    itemLink(item) {
      if (item.static) return this.basePath + this.activeSlug + '/'
      const catSlug = this.categorySlugForItem(item) || this.activeSlug
      const sub = this.subSlug || item.itemSlug || ''
      const prefix = this.basePath + catSlug + '/'
      if (sub && !item.picUrl) return prefix + sub + '/'
      return prefix + item.id + '.html'
    },

    categorySlugForItem(item) {
      if (!item) return ''
      const cat = this.categories.find(c => Number(c.id) === Number(item.categoryId))
      if (!cat) return ''
      return REVERSE_SLUG_ALIASES[cat.categorySlug] || cat.categorySlug
    },

    matchesCategory(item) {
      if (this.isAllCases) return true
      const cat = this.activeCategory
      if (!cat) return false
      return item.categoryId === cat.id
    },

    matchesSubSlug(item) {
      if (!this.subSlug) return true
      if (/^\d+$/.test(String(this.subSlug))) {
        return String(item.id) === String(this.subSlug)
      }
      const sub = this.subItems.find(s => s.itemSlug === this.subSlug)
      if (sub && item.subCategoryId) return item.subCategoryId === sub.id
      return item.itemSlug === this.subSlug ||
        String(item.itemSlug || '').startsWith(this.subSlug + '-')
    },

    async loadCategories() {
      try {
        const res = await listSolutionCategory({
          categoryType: this.categoryType,
          status: 1,
          pageNum: 1,
          pageSize: 50
        }, this.$i18n.locale)
        this.categories = unwrapRows(res)
          .slice()
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || Number(a.id) - Number(b.id))
      } catch (e) {
        this.categories = []
      }

      if (!this.solutionSlug) {
        if (this.mode === 'case') {
          this.$router.replace(this.basePath + 'all/')
        } else if (this.navItems.length) {
          this.$router.replace(this.basePath + this.navItems[0].slug + '/')
        } else {
          await this.loadItems()
        }
      } else {
        await this.loadItems()
      }
    },

    async loadItems() {
      if (!this.isAllCases && !this.activeCategory) {
        this.caseItems = []
        this.subItems = []
        return
      }
      try {
        const query = {
          status: 1,
          pageNum: 1,
          pageSize: this.isAllCases ? 500 : 200
        }
        if (!this.isAllCases) query.categoryId = this.activeCategory.id
        const res = await listSolutionItem(query, this.$i18n.locale)
        const rows = unwrapRows(res)
        // Case pages: every published row is a visible case card (pic optional)
        if (this.mode === 'case') {
          this.caseItems = rows.slice()
          this.subItems = this.isAllCases ? [] : rows.slice()
        } else {
          this.subItems = rows.filter(item => !item.picUrl)
          this.caseItems = rows.filter(item => !!item.picUrl)
        }
        if (!this.caseItems.length && !this.isAllCases) {
          const allRes = await listSolutionItem({ status: 1, pageNum: 1, pageSize: 500 }, this.$i18n.locale)
          const allRows = unwrapRows(allRes).filter(item => item.categoryId === this.activeCategory.id)
          if (this.mode === 'case') {
            this.caseItems = allRows
            this.subItems = allRows
          } else {
            this.subItems = allRows.filter(item => !item.picUrl)
            this.caseItems = allRows.filter(item => !!item.picUrl)
          }
        }
      } catch (e) {
        this.caseItems = []
        this.subItems = []
      }
    }
  }
}
</script>
